import { CONTINUOUS_EDUCATION } from '../../../api/endpoints';
import request from '../../../api/request';
import {
  NonStructuredAcademicOffer,
  NonStructuredAcademicOfferFilters,
  NonStructuredAcademicOfferFormData,
  NonStructuredAcademicOfferPaginatedResponse,
  NonStructuredAcademicOfferScheduleResponse,
  NonStructuredAcademicOfferStepEnum,
  NonStructuredAcademicOfferStepResponse,
  NonStructuredAcademicOfferSummaryResponse,
  CreateNonStructuredAcademicOfferPayload,
  PublishNonStructuredAcademicOfferPayload,
  UpdateNonStructuredAcademicOfferPayload,
  UpdateNonStructuredAcademicOfferSummaryPayload,
} from './types';

/**
 * Obtiene los datos necesarios para el formulario de creación/edición
 */
export const getAcademicOfferFormData = () => {
  return request<NonStructuredAcademicOfferFormData>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFERS_FORM_DATA,
  );
};

/**
 * Obtiene el listado paginado de ofertas académicas de educación continua
 */
export const getAcademicOffers = (
  filters: NonStructuredAcademicOfferFilters = {},
) => {
  return request<NonStructuredAcademicOfferPaginatedResponse>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFERS,
    {
      method: 'GET',
      params: filters,
    },
  );
};

/**
 * Obtiene una oferta académica por ID
 */
export const getAcademicOfferById = (id: number | string) => {
  return request<NonStructuredAcademicOffer>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER(id),
  );
};

/**
 * Crea una nueva oferta académica
 */
export const createAcademicOffer = (
  data: CreateNonStructuredAcademicOfferPayload,
) => {
  return request<NonStructuredAcademicOffer>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFERS,
    {
      method: 'POST',
      data,
    },
  );
};

/**
 * Actualiza una oferta académica existente
 */
export const updateAcademicOffer = (
  id: number | string,
  data: UpdateNonStructuredAcademicOfferPayload,
) => {
  return request<NonStructuredAcademicOffer>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER(id),
    {
      method: 'PUT',
      data,
    },
  );
};

/**
 * Elimina una oferta académica
 */
export const deleteAcademicOffer = (id: number | string) => {
  return request(CONTINUOUS_EDUCATION.ACADEMIC_OFFER(id), {
    method: 'DELETE',
  });
};

/**
 * Obtiene el paso actual de una oferta académica de EC
 */
export const getAcademicOfferCurrentStep = (id: number | string) => {
  return request<NonStructuredAcademicOfferStepResponse>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER_CURRENT_STEP(id),
  );
};

/**
 * Actualiza el paso actual de una oferta académica de EC
 */
export const updateAcademicOfferCurrentStep = (
  id: number | string,
  currentStep: NonStructuredAcademicOfferStepEnum,
) => {
  return request<NonStructuredAcademicOfferStepResponse>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER_CURRENT_STEP(id),
    {
      method: 'PUT',
      data: { currentStep },
    },
  );
};

/**
 * Obtiene los datos de horarios agregados de una oferta CE
 */
export const getAcademicOfferScheduleData = (id: number | string) => {
  return request<NonStructuredAcademicOfferScheduleResponse>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER_SCHEDULE(id),
  );
};

/**
 * Obtiene los datos del resumen de una oferta CE
 */
export const getAcademicOfferSummaryData = (id: number | string) => {
  return request<NonStructuredAcademicOfferSummaryResponse>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER_SUMMARY(id),
  );
};

/**
 * Publica una oferta CE: completa los datos, activa la oferta y crea el proceso de admisión
 */
export const publishAcademicOffer = (
  id: number | string,
  data: PublishNonStructuredAcademicOfferPayload,
) => {
  return request<NonStructuredAcademicOffer>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER_PUBLISH(id),
    {
      method: 'POST',
      data,
    },
  );
};

/**
 * Actualiza los datos de resumen de una oferta CE activa
 */
export const updateAcademicOfferSummary = (
  id: number | string,
  data: UpdateNonStructuredAcademicOfferSummaryPayload,
) => {
  return request<NonStructuredAcademicOffer>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER_SUMMARY(id),
    {
      method: 'PUT',
      data,
    },
  );
};

/**
 * Actualiza los cupos de múltiples ofertas NE en bulk
 */
export const updateAcademicOffersQuotas = (
  items: { id: number; quota: number }[],
) => {
  return request(CONTINUOUS_EDUCATION.ACADEMIC_OFFERS_QUOTAS, {
    method: 'PATCH',
    data: { items },
  });
};

/**
 * Cierra una oferta CE activa
 */
export const closeAcademicOffer = (id: number | string) => {
  return request<NonStructuredAcademicOffer>(
    CONTINUOUS_EDUCATION.ACADEMIC_OFFER_CLOSE(id),
    {
      method: 'PATCH',
    },
  );
};
